import { Clock, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { DeliveryFeeZones } from "@/types/listing";
import FeesAndDeliveryBlock from "./FeesAndDeliveryBlock";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels, commonDateLabels } from "@/i18n";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface TimeRemainingProps {
  endTime: string;
  price: number;
  saleType?: "auction" | "direct";
  deliveryFee?: DeliveryFeeZones;
  isOwnAuction?: boolean;
}

const TimeRemaining = ({
  endTime,
  price,
  saleType = "auction",
  deliveryFee,
  isOwnAuction = false,
}: TimeRemainingProps) => {
  const timeRemaining = useAuctionTimer(endTime);

  if (!timeRemaining) {
    return null;
  }

  return (
    <>
      <div className="flex flex-col items-start gap-2">
        <FeesAndDeliveryBlock
          price={price}
          deliveryFee={deliveryFee}
          hideWhenOwn={isOwnAuction}
        />
        <AuctionTimerDisplay timeRemaining={timeRemaining} />
      </div>
    </>
  );
};

export const isTimerZero = (t: {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}) => t.days === 0 && t.hours === 0 && t.minutes === 0 && t.seconds === 0;

const isZero = isTimerZero;

/**
 * Shown while countdown is at 0 but listing.status is still "active"
 * (backend cron has not finalized the auction yet).
 */
export function AuctionFinalizingIndicator({
  compact = false,
  className,
}: {
  compact?: boolean;
  className?: string;
}) {
  const { language } = useLanguage();
  const bi = carDetailsLabels(language).biddingInfo;

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/[0.06]",
        compact ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm",
        className,
      )}
      role="status"
      aria-live="polite"
      title={bi.finalizingHint ?? "Waiting for the auction to finish"}
    >
      <Loader2
        className={cn(
          "shrink-0 animate-spin text-amber-700 dark:text-amber-400",
          compact ? "h-3.5 w-3.5" : "h-4 w-4",
        )}
        aria-hidden
      />
      <span className="font-medium text-amber-800 dark:text-amber-300">
        {bi.finalizing ?? "Finalizing…"}
      </span>
    </div>
  );
}

const isUrgent = (t: {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}) => !isZero(t) && t.days === 0 && t.hours === 0;

/** Timer display only (countdown). Exported so BiddingInfo can place buttons under price, then this under status. */
export function AuctionTimerDisplay({
  timeRemaining,
  compact = false,
}: {
  timeRemaining: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
  /** Tighter sizing for list cards (CarCard). */
  compact?: boolean;
}) {
  const { language } = useLanguage();
  const dateLabels = commonDateLabels(language);
  const d = dateLabels.dayShort ?? "d";
  const h = dateLabels.hourShort ?? "h";
  const min = dateLabels.minShort ?? "min";
  const sec = dateLabels.secShort ?? "sec";
  const ended = isZero(timeRemaining);
  const urgent = isUrgent(timeRemaining);

  const unitClass = cn(
    "tabular-nums font-semibold tracking-tight",
    compact ? "text-xs" : "text-base md:text-sm",
    ended
      ? "text-red-600 dark:text-red-400"
      : urgent
        ? "text-amber-700 dark:text-amber-400"
        : "text-foreground",
  );
  const labelClass = cn(
    // Same size as numbers; lighter weight + muted color.
    "tabular-nums font-normal ml-0.5",
    compact ? "text-xs" : "text-base md:text-sm",
    ended
      ? "text-red-600/70 dark:text-red-400/70"
      : urgent
        ? "text-amber-700/70 dark:text-amber-400/70"
        : "text-muted-foreground",
  );

  return (
    <div
      className={cn(
        // Content-sized pill (not full-bleed stretch) — a bit roomier than the old tiny timer.
        "inline-flex items-center rounded-md border max-w-full",
        compact
          ? "gap-1.5 px-2 py-1"
          : "gap-2 px-3 py-2 md:px-2.5 md:py-1.5",
        ended
          ? "border-red-500/30 bg-red-500/[0.04]"
          : urgent
            ? "border-amber-500/25 bg-amber-500/[0.04]"
            : "border-border bg-muted/40",
      )}
    >
      <Clock
        className={cn(
          "shrink-0",
          compact ? "w-3.5 h-3.5" : "w-5 h-5 md:w-4 md:h-4",
          ended
            ? "text-red-600 dark:text-red-400"
            : urgent
              ? "text-amber-700 dark:text-amber-400"
              : "text-muted-foreground",
        )}
        aria-hidden
      />
      <div className="flex items-center gap-0 min-w-0 divide-x divide-border/80">
        {(
          [
            [timeRemaining.days, d, false],
            [timeRemaining.hours, h, false],
            [timeRemaining.minutes, min, false],
            [timeRemaining.seconds, sec, true],
          ] as const
        ).map(([value, unit, isSeconds], i) => (
          <div
            key={unit}
            className={cn(
              "flex items-baseline",
              compact ? "px-1.5" : "px-2 md:px-1.5",
              i === 0 && "pl-0",
            )}
          >
            <span
              className={cn(
                unitClass,
                isSeconds &&
                  !ended &&
                  "text-red-500 dark:text-red-400",
              )}
            >
              {value}
            </span>
            <span
              className={cn(
                labelClass,
                isSeconds &&
                  !ended &&
                  "text-red-500/70 dark:text-red-400/70",
              )}
            >
              {unit}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Hook that returns time remaining or null. Used by AuctionTimer. */
export function useAuctionTimer(endTime: string) {
  const [timeRemaining, setTimeRemaining] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);

  useEffect(() => {
    const calculate = () => {
      const now = new Date().getTime();
      const end = new Date(endTime).getTime();
      const diff = end - now;
      if (diff <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setTimeRemaining({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      });
    };
    calculate();
    const interval = setInterval(calculate, 1000);
    return () => clearInterval(interval);
  }, [endTime]);

  return timeRemaining;
}

/** Compact one-line timer (no border box). Same font size for numbers and unit letters. */
export function CompactAuctionTimer({
  endTime,
  className = "",
}: {
  endTime: string;
  className?: string;
  /** @deprecated Ignored — timer uses neutral/urgent styling. */
  useGreen?: boolean;
}) {
  const timeRemaining = useAuctionTimer(endTime);
  if (!timeRemaining) return null;
  return (
    <CompactAuctionTimerView
      timeRemaining={timeRemaining}
      className={className}
    />
  );
}

function CompactAuctionTimerView({
  timeRemaining,
  className = "",
}: {
  timeRemaining: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
  className?: string;
}) {
  const { language } = useLanguage();
  const dateLabels = commonDateLabels(language);
  const d = dateLabels.dayShort ?? "d";
  const h = dateLabels.hourShort ?? "h";
  const min = dateLabels.minShort ?? "min";
  const sec = dateLabels.secShort ?? "sec";
  const ended = isZero(timeRemaining);
  const urgent = isUrgent(timeRemaining);
  const tone = ended
    ? "text-red-600 dark:text-red-400"
    : urgent
      ? "text-amber-700 dark:text-amber-400"
      : "text-foreground";
  // Same size as numbers, but normal weight + lighter shade.
  const unitClass = ended
    ? "font-normal text-red-600/70 dark:text-red-400/70"
    : urgent
      ? "font-normal text-amber-700/70 dark:text-amber-400/70"
      : "font-normal text-muted-foreground";

  return (
    <div
      className={cn(
        "flex items-center gap-1 text-xs tabular-nums font-medium leading-none",
        tone,
        className,
      )}
    >
      <Clock
        className={cn("w-3 h-3 flex-shrink-0", unitClass)}
        aria-hidden
      />
      <span className="whitespace-nowrap">
        {timeRemaining.days}
        <span className={unitClass}>{d}</span>{" "}
        {timeRemaining.hours}
        <span className={unitClass}>{h}</span>{" "}
        {timeRemaining.minutes}
        <span className={unitClass}>{min}</span>{" "}
        <span
          className={cn(
            "font-medium",
            ended
              ? "text-red-600 dark:text-red-400"
              : "text-red-500 dark:text-red-400",
          )}
        >
          {timeRemaining.seconds}
          <span className="font-normal opacity-80">{sec}</span>
        </span>
      </span>
    </div>
  );
}

/** Standalone auction timer (countdown only). Use in BiddingInfo after price + buttons + reserve status. */
export function AuctionTimer({
  endTime,
  className,
  compact = false,
}: {
  endTime: string;
  className?: string;
  compact?: boolean;
}) {
  const isMobile = useIsMobile();
  const timeRemaining = useAuctionTimer(endTime);
  if (!timeRemaining) return null;

  // Mobile / list: one-line collapsed style (no box) so it doesn't eat vertical space.
  if (compact || isMobile) {
    return (
      <CompactAuctionTimerView
        timeRemaining={timeRemaining}
        className={cn(
          // Match collapsed bidding bar size on mobile detail; keep cards smaller via compact.
          compact ? "text-sm font-medium" : "text-lg font-medium",
          className,
        )}
      />
    );
  }

  return (
    <div className={className}>
      <AuctionTimerDisplay timeRemaining={timeRemaining} />
    </div>
  );
}

export default TimeRemaining;
